<template>
  <i
    class="icon-container"
    @click="onClick"
    :aria-label="resolveAriaLabel()"
    role="img"
    :style="iconContainerStyles"
  >
    <span v-if="badgeContent" class="body-xs badge">
      {{ resolveBadgeContent }}
    </span>
    <svg :class="`icon-${name}`" :aria-hidden="ariaHidden">
      <use :href="iconId" />
    </svg>
  </i>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import svgSprite from './sprite.svg'

interface IProps {
  name: string
  /**
   *  ----- Styles -----
   */
  colorFill?: string
  colorHover?: string
  size?: string
  rotateDeg?: number
  /**
   *  ----- Other options -----
   */
  isClickable?: boolean
  ariaLabel?: string // Specifies the label to use for accessibility. Defaults to the icon name.
  ariaHidden?: boolean // Set the icon to hidden, respectively `true`, to remove it from the accessibility tree.
  badgeContent?: number
}

const props = withDefaults(defineProps<IProps>(), {
  size: '24px',
  rotateDeg: 0,
  isClickable: false,
  ariaHidden: true,
  badgeContent: 0,
  colorFill: '',
  colorHover: '',
  ariaLabel: '',
})

const emit = defineEmits(['clicked'])

const iconId = computed((): string => `${svgSprite}#${props.name}`)

const iconContainerStyles = computed(() => ({
  '--icon-size': props.size,
  '--icon-svg-padding': `calc(${props.size} * 0.085)`,
  '--rotate-deg': `${props.rotateDeg}deg`,
  '--color-hover': props.colorHover || props.colorFill,
  '--icon-fill': props.colorFill,
  cursor: props.isClickable ? 'pointer' : 'auto',
}))

const resolveBadgeContent = computed((): string => {
  const content = props.badgeContent
  return content > 99 ? '99+' : content.toString()
})

function onClick(): void {
  if (!props.isClickable) return
  emit('clicked')
}

function resolveAriaLabel(): string {
  return props.ariaLabel || props.name
}
</script>

<style lang="scss" scoped>
.icon-container {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: var(--icon-size);
  height: var(--icon-size);

  svg {
    // width & height needed on container and svg to prevent sizing problems
    width: 100%;
    height: 100%;
    // Using margin here because width & height includes padding
    margin: var(--icon-svg-padding);
    transform: rotate(var(--rotate-deg));
    fill: var(--icon-fill);

    &:hover {
      fill: var(--color-hover);
    }
  }

  .badge {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background-color: var(--color-brand-primary-base);
    border-radius: var(--icon-size);
    color: var(--color-font-white);
    font-size: calc(var(--icon-size) / 2.4);
    font-style: normal;
    line-height: 1.6;
    height: calc(var(--icon-size) / 1.5);
    min-width: calc(var(--icon-size) / 1.5);
    padding: 0 calc(var(--icon-size) / 6);
    text-align: center;
    position: absolute;
    top: 0;
    left: 50%;
    z-index: 1;
  }
}
</style>
